/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { ApolloProvider } from '@apollo/client';
import { MantineProvider, rem } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import SuperTokens from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';
import ThirdPartyEmailPassword from 'supertokens-auth-react/recipe/thirdpartyemailpassword';

import '@fai/friendli-suite/features/i18n';
import './main.css';

import '@fai/ui/fonts/Inter-variable/stylesheet.css';
import '@fai/ui/fonts/Saans/stylesheet.css';
import client from '@fai/friendli-suite/shared/apollo/base-link';
import { environment } from '@fai/friendli-suite/shared/environments/environment';
import { theme } from '@fai/theme';
import '@fai/friendli-suite/app/init-sentry';
import '@fai/friendli-suite/app/init-amplitude';

import App from './app';
import reportWebVitals from './app/report-web-vitals';
import { replaceMintlifyWidgetInjectedStyles } from './shared/mintlify/replace-mintlify-widget-injected-styles';

dayjs.extend(utc);
dayjs.extend(minMax);

// Checkout https://supertokens.com/docs/website/4.2.x/graphql
SuperTokens.init({
  appInfo: {
    apiDomain: environment.SERVER_URL,
    apiBasePath: environment.AUTH_PATH,
    appName: environment.APP_NAME,
    websiteDomain: window.location.origin,
    websiteBasePath: environment.LOGIN_PATH,
  },
  recipeList: [
    Session.init({
      tokenTransferMethod: 'cookie',
    }),
    ThirdPartyEmailPassword.init({}),
  ],
});

replaceMintlifyWidgetInjectedStyles();

const root = createRoot(document.querySelector('#root') as HTMLElement);

const queryClient = new QueryClient();

const stripePromise = loadStripe(environment.STRIPE_PUBLISHABLE_KEY);

const options: StripeElementsOptions = {
  currency: 'usd',
  mode: 'setup',
  paymentMethodTypes: ['card'],
};

const LDContent = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApolloProvider client={client}>
        <MantineProvider defaultColorScheme="light" theme={theme}>
          <Elements stripe={stripePromise} options={options}>
            <Notifications
              limit={5}
              position="top-center"
              styles={{ root: { width: 'unset', maxWidth: rem(375) } }}
              zIndex={1000}
            />
            <App />
          </Elements>
        </MantineProvider>
      </ApolloProvider>
    </QueryClientProvider>
  );
};

const LDProvider = withLDProvider({
  clientSideID: environment.LAUNCH_DARKLY_CLIENT_ID,
  context: {
    kind: 'multi',
    user: { kind: 'user', anonymous: true },
    team: { kind: 'team', anonymous: true },
    // device
  },
})(LDContent);

root.render(
  <StrictMode>
    <LDProvider />
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// LSY: added this to test transactions
reportWebVitals();
